import {ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, OnChanges, SimpleChanges} from '@angular/core';
import {IMyDate} from 'angular-mydatepicker';
import {FormFieldConfigInterface} from "../../../form/components/abstract/abstract-form-field.component";
import {UntypedFormGroup} from "@angular/forms";
import * as moment from 'moment/moment';
import {TranslateService} from "../../../../services/translate/translate.service";
import {DateTimeAdapter} from "ng-pick-datetime-ex";

@Component({
    selector: 'date-input',
    templateUrl: './date-input.component.html'
})
export class DateInputComponent implements OnInit, OnChanges {
    @ViewChild('dateInput', {static: false}) private datePicker: ElementRef;
    @ViewChild('timePickerInput', {static: false}) private timePicker: ElementRef;
    @ViewChild('dateTimePickerInput', {static: false}) private dateTimePicker: ElementRef;

    @Input() inputType: 'date' | 'time' | 'datetime' = 'date';
    @Input() disabled: boolean = false;
    @Input() readonly: boolean = false;
    @Input() allowClear: boolean = true;
    @Input() batchUpdateMode: boolean = false;
    @Input() parentFormGroup:UntypedFormGroup | null = null;
    @Input() config: FormFieldConfigInterface | null = null;
    @Output() onDateChanged: EventEmitter<Date> = new EventEmitter();

    dateTimeMinValue: Date = null;
    dateTimeMaxValue: Date = null;
    isRequired: boolean = false;
    input: ElementRef<HTMLInputElement> = null;
    dateTimeModel;

    constructor(
        public changeDetectorRef: ChangeDetectorRef,
        private translateService: TranslateService,
        private dateTimeAdapter: DateTimeAdapter<any>
    ) {}

    ngOnChanges(changes: SimpleChanges) {
        const currentValue = changes['initialValue']?.currentValue ?? null;

        if(currentValue && typeof currentValue !== 'undefined'){
            this.dateTimeModel = '';
            if(this.isTimeOnly(currentValue)){ //Time only
                this.dateTimeModel = new Date('1970-01-01 '+currentValue);
            } else { //Datetime
                if(currentValue.year && typeof currentValue.year !== 'undefined' && currentValue.month && typeof currentValue.month !== 'undefined' && currentValue.day && typeof currentValue.day !== 'undefined'){ //As object
                    // Use numeric Date constructor to avoid locale-dependent string parsing
                    this.dateTimeModel = new Date(currentValue.year, currentValue.month - 1, currentValue.day);
                } else { //As string
                    // Use moment to parse as local time, avoiding UTC interpretation of date-only strings
                    this.dateTimeModel = moment(currentValue).toDate();
                }
            }
        }
    }

    @Input() set minDate(date: IMyDate) {
        if (date !== null) {
            this.dateTimeMinValue = new Date(date.year, date.month, date.day);
            this.changeDetectorRef.detectChanges();
        }
    }

    @Input() set maxDate(date: IMyDate) {
        if (date !== null) {
            this.dateTimeMaxValue = new Date(date.year, date.month, date.day);
            this.changeDetectorRef.detectChanges();
        }
    }

    @Input() set initialValue(initialValue: string | IMyDate | null | undefined) {
        if (initialValue) {
            if(typeof initialValue === "string") {
                const parsedMoment = moment(initialValue);
                if(parsedMoment.isValid()){
                    // Use moment to parse as local time, avoiding UTC interpretation of date-only strings
                    this.dateTimeModel = parsedMoment.toDate();
                } else { //Might be time only, set starting date
                    this.dateTimeModel = new Date('1970-01-01T'+initialValue)
                }
            } else {
                this.dateTimeModel = new Date(initialValue.year, initialValue.month - 1, initialValue.day);
            }
        }
        setTimeout(() => {
            this.changeDetectorRef.detectChanges();
        });
    }

    ngOnInit() {
        // Set locale before anything else to ensure correct date format display
        this.dateTimeAdapter.setLocale(TranslateService.LOCALE);
        moment.locale(TranslateService.LOCALE);

        const originalParse = this.dateTimeAdapter.parse.bind(this.dateTimeAdapter);
        this.dateTimeAdapter.parse = (value: any, parseFormat?: any): Date | null => {
            // Only apply the locale date-format ('L') parsing for date/datetime inputs.
            // For time-only inputs 'L' is a date format and would parse e.g. "14:30" to
            // an unintended midnight (00:00) date, resetting the time. Use native parsing instead.
            if (this.inputType !== 'time' && typeof value === 'string' && value.trim()) {
                const parsed = moment(value.trim(), 'L');
                if (parsed.isValid()) {
                    return parsed.toDate();
                }
            }
            const nativeResult = originalParse(value, parseFormat);
            return nativeResult;
        };

        this.input = this.datePicker;
        this.readConfig();
    }

    ngAfterViewInit() {
        this.readConfig();
    }

    private isTimeOnly(value: string): boolean {
        return /^\d{1,2}:\d{2}(:\d{2})?$/.test(value);
    }

    clearDateTime() {
        if(!this.disabled && this.allowClear) {
            switch(this.inputType) {
                case "date": {
                    this.datePicker.nativeElement.value = "";
                    break;
                }
                case "time": {
                    this.timePicker.nativeElement.value = "";
                    break;
                }
                case "datetime": {
                    this.dateTimePicker.nativeElement.value = "";
                    break;
                }
            }
            this.dateTimeModel = "";
            this.changeDetectorRef.detectChanges();
        }
    }

    getInitialInputForDisabledElement():string {
        let returnString = "";
        const locale = TranslateService.LOCALE;
        if(this.dateTimeModel) {
            switch (this.inputType) {
                case "date": {
                    returnString = new Date(this.dateTimeModel)
                        .toLocaleString(locale, {year: "numeric", month: "2-digit", day: "2-digit"});
                    break;
                }
                case "time": {
                    returnString = new Date(this.dateTimeModel)
                        .toLocaleString(locale, {hour: "2-digit", minute: "2-digit"});
                    break;
                }
                case "datetime": {
                    returnString = new Date(this.dateTimeModel)
                        .toLocaleString(locale, {
                            year: "numeric",
                            month: "2-digit",
                            day: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit"
                        });
                    break;
                }
            }
        }
        return returnString;
    }

    public onDatePicked(event) {
        const dateValue = event.value;
        
        if (dateValue && moment(dateValue).isValid()) {
            this.dateTimeModel = moment(dateValue).format('YYYY-MM-DDTHH:mm:ss');
        } else {
            this.dateTimeModel = null;
        }

        this.onDateChanged.emit(this.dateTimeModel);
        this.changeDetectorRef.detectChanges();
    }

    private readConfig(): void {
        if (this.config) {
            if (this.batchUpdateMode && this.parentFormGroup) {
                this.parentFormGroup.controls[this.config.name].disable();
            }

            if (this.config.attr && this.config.attr.allowPastDates == false) {
                let today: Date = new Date();
                today.setDate(today.getDate() - 1);
                this.minDate = {
                    year: today.getFullYear(),
                    month: today.getMonth(),
                    day: today.getDate()
                };
            }


            this.isRequired = this.config.required;
        }

        setTimeout(() => {
            this.changeDetectorRef.detectChanges();
        });
    }
}
